import { z } from "zod";

import { inviteSchema } from "api/schemas/createShareSettingsApiSchema";
import { imageFormatSchema } from "api/schemas/imageCollectionApiSchema";
import { nullToUndefined } from "api/utils";
import { DestinationTypes } from "domains/types";
import { CustomMapOwner } from "entities/topics/actions";

export const parentTopicSchema = z.object({
  co_curator: z.string().optional(),
  id: z.string(),
  name: z.string(),
});

export const topicAttachmentSchema = z.object({
  id: z.string(),
  is_shareable: z.boolean().optional(),
  mimetype: z.string(),
  title: z.string(),
  type: z.literal("podcast"),
  url: z.string().optional(),
});

export const topicProviderSchema = z.object({
  id: z.string(),
  name: z.string(),
});

export const topicPermissionLabelSchema = z.enum(["editor", "owner", "public", "viewer"]).optional();

const topicStatusSchema = z.enum(["Active", "Inactive", "Draft", "Idea"]);

export const topicSchema = z.object({
  attachment: nullToUndefined(topicAttachmentSchema),
  can_edit: nullToUndefined(z.boolean()),
  can_make_public: nullToUndefined(z.boolean()),
  co_curator: nullToUndefined(z.string()),
  co_curator_id: nullToUndefined(z.string()),
  co_curator_status: nullToUndefined(z.enum(["Active", "Inactive"])),
  description: nullToUndefined(z.string()),
  description_original: nullToUndefined(z.string()),
  fullsize_map_image_url: nullToUndefined(z.string()),
  has_dialogue: nullToUndefined(z.boolean()),
  has_unauthenticated_content_access: nullToUndefined(z.boolean()),
  id: z.string(),
  image_url: nullToUndefined(z.string()),
  images: nullToUndefined(imageFormatSchema),
  is_curated: nullToUndefined(z.boolean()),
  is_custom: nullToUndefined(z.boolean()),
  is_following: nullToUndefined(z.boolean()),
  is_highlighted: nullToUndefined(z.boolean()),
  is_machine_translated: nullToUndefined(z.boolean()),
  is_new: nullToUndefined(z.boolean()),
  is_public: nullToUndefined(z.boolean()),
  is_shareable: nullToUndefined(z.boolean()),
  name: z.string(),
  name_original: nullToUndefined(z.string()),
  parent_topic: nullToUndefined(z.string()),
  permission: nullToUndefined(z.string()),
  provider: nullToUndefined(
    z.object({
      id: z.string(),
      name: z.string(),
    }),
  ),
  source: nullToUndefined(z.string()),
  status: nullToUndefined(topicStatusSchema),
  subtitle: nullToUndefined(z.string()),
  subtitle_original: nullToUndefined(z.string()),
  type: nullToUndefined(z.string()),
  unaccented_lowercase_name: nullToUndefined(z.string()),
});

export const topicPayloadSchema = z.object({
  id: z.string(),
});

export const topicFulfilledPayloadSchema = z.object({
  topic: topicSchema,
});

export const topicListPayloadSchema = z.object({
  canEdit: z.boolean().optional(),
  limit: z.number(),
  offset: z.number(),
  owner: z.nativeEnum(CustomMapOwner).optional(),
  topics: z.array(topicSchema).optional(),
  type: z.nativeEnum(DestinationTypes),
});

export const followTopicPayloadSchema = z.object({
  id: z.string(),
  is_following: z.boolean().optional(),
  isFollowing: z.boolean().optional(),
});

export const fetchTopicsPayloadSchema = z.object({
  topics: z.array(topicSchema),
});

export const fetchFollowedTopicsPayloadSchema = z.object({
  limit: z.number(),
  offset: z.number(),
  topics: z.array(topicSchema),
});

export const fetchCustomTopicsPayloadSchema = z.object({
  limit: z.number(),
  offset: z.number(),
  topicIds: z.string().array(),
});

export const fetchTopicsForCollectionPayloadSchema = z.object({
  id: z.string(),
  limit: z.number(),
  offset: z.number(),
});

const topicInvitationsSchema = z.object({
  shared_users: z.array(inviteSchema),
});

const topicBaseSchema = z.object({
  description: z.string(),
  name: z.string(),
  status: topicStatusSchema,
  subtitle: z.string().optional(),
  type: z.string(),
});

const topicCreateSchema = topicBaseSchema.extend({ dimension_ids: z.string().array() });

const topicUpdateSchema = topicCreateSchema.extend({ id: z.string() });

export const topicPostSchema = z.object({
  invitations: topicInvitationsSchema.optional(),
  topic: topicCreateSchema,
});

export const topicPutSchema = z.object({
  invitations: topicInvitationsSchema.optional(),
  topic: topicUpdateSchema,
});

export const topicMetaDataTypeSchema = z.object({
  count: z.number(),
  id: z.string().optional(),
  label: z.string().optional(),
  name: z.string(),
  plural_name: z.string(),
  type: z.string(),
});

const topicMetadataSectionSchema = z.object({
  count: z.number(),
  types: z.array(topicMetaDataTypeSchema),
});

export const topicMetadataResultSchema = z.object({
  all: topicMetaDataTypeSchema,
  curated: topicMetadataSectionSchema,
  significant: topicMetadataSectionSchema,
  uncurated: topicMetadataSectionSchema,
});
